import { useEffect, useMemo, useState } from "react";
import BookingsKillSwitch from "../../components/BookingsKillSwitch";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Plus, Trash2, Check, X as XIcon, Pencil, Euro, Loader2, CalendarClock } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { todayLocalIso } from "../../lib/dateUtils";
import useBulkSelect from "../../lib/useBulkSelect";
import BulkActionBar, { BulkCheckbox } from "../../components/BulkActionBar";
import RescheduleModal from "../../components/RescheduleModal";
import CleaningConsumptionModal from "../../components/CleaningConsumptionModal";
import InvoicePreviewModal from "../../components/InvoicePreviewModal";

const EXTRAS_ICONS = {
  lavandaria: "🧺", consumiveis: "🧻", limpeza_profunda: "🧼", churrasqueira: "🔥", relva: "🌿", manutencao: "🔧",
};
const STATUS_LABEL = {
  pending: "Pendente", confirmed: "Confirmado", in_progress: "Em curso",
  completed: "Concluído", cancelled: "Cancelado",
};

/** Status filter tabs shown at the top of the bookings list.
 * Order matches the operational lifecycle: pending → confirmed → in_progress → completed
 * + cancelled as a terminal state, + "all" reset. */
const FILTERS = [
  { key: "overdue", label: "Atrasadas", tone: "blocked" },
  { key: "pending", label: "Pendentes", tone: "pending" },
  { key: "pending_payment", label: "Por pagar", tone: "blocked" },
  { key: "confirmed", label: "Confirmadas", tone: "confirmed" },
  { key: "in_progress", label: "Em curso", tone: "confirmed" },
  { key: "paid", label: "Pagas", tone: "completed" },
  { key: "completed", label: "Concluídas", tone: "completed" },
  { key: "cancelled", label: "Canceladas", tone: "blocked" },
  { key: "all", label: "Todas", tone: "neutral" },
];

export default function AdminBookings() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [extrasCatalog, setExtrasCatalog] = useState([]);
  const [expanded, setExpanded] = useState({}); // { [id]: bool }
  const [filter, setFilter] = useState("pending");
  const [editingBooking, setEditingBooking] = useState(null);
  const [consumptionBookingId, setConsumptionBookingId] = useState(null);
  const [previewBookingId, setPreviewBookingId] = useState(null);
  const [searchParams] = useSearchParams();
  const todayStr = useMemo(() => todayLocalIso(), []);

  // Honour ?filter= deep-links (e.g. dashboard's "Reservas atrasadas" tile)
  useEffect(() => {
    const f = searchParams.get("filter");
    if (f && ["overdue", "pending", "pending_payment", "confirmed", "in_progress", "paid",
              "completed", "cancelled", "all"].includes(f)) {
      setFilter(f);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const load = () => api.get("/bookings").then(({ data }) => setItems(data.items));

  useEffect(() => {
    load();
    api.get("/admin/users", { params: { role: "employee" } }).then(({ data }) => setEmployees(data.items.filter(e => e.approved)));
    api.get("/catalog/extras").then(({ data }) => setExtrasCatalog(data.items));
    // Auto-refresh reservas a cada 20s
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, []);

  // Count per status (source of truth for the badges)
  const counts = useMemo(() => {
    const acc = {
      overdue: 0, pending: 0, pending_payment: 0, confirmed: 0, in_progress: 0, paid: 0,
      completed: 0, cancelled: 0, all: items.length,
    };
    for (const b of items) {
      if (acc[b.status] !== undefined) acc[b.status]++;
      // «Pagas» = reservas com pagamento confirmado (independente do
      // estado da limpeza). É a fonte de verdade para o dashboard
      // financeiro — só estas contam para receitas.
      if (b.payment_status === "paid") acc.paid++;
      // «Por pagar» = reservas ativas (não canceladas/refunded) com
      // pagamento ainda pendente. Inclui «pendentes», «confirmadas» e
      // «em curso» — qualquer reserva onde o cliente ainda deve dinheiro.
      if (b.payment_status !== "paid"
          && !["cancelled", "refunded", "no_show"].includes(b.status)) {
        acc.pending_payment++;
      }
      // Overdue = scheduled date is in the past AND not yet concluded.
      if (b.scheduled_date < todayStr
          && ["pending", "confirmed", "in_progress"].includes(b.status)) {
        acc.overdue++;
      }
    }
    return acc;
  }, [items, todayStr]);

  const visible = useMemo(() => {
    if (filter === "all") return items;
    if (filter === "overdue") {
      return items.filter(b =>
        b.scheduled_date < todayStr
        && ["pending", "confirmed", "in_progress"].includes(b.status),
      );
    }
    if (filter === "paid") {
      // Sort most recently paid first so the admin sees the latest
      // confirmations at the top of the list.
      return items
        .filter(b => b.payment_status === "paid")
        .slice()
        .sort((a, b) => (b.paid_at || "").localeCompare(a.paid_at || ""));
    }
    if (filter === "pending_payment") {
      // Show oldest scheduled_date first — the «most overdue» payments
      // bubble to the top so the admin chases them first.
      return items
        .filter(b => b.payment_status !== "paid"
                     && !["cancelled", "refunded", "no_show"].includes(b.status))
        .slice()
        .sort((a, b) => (a.scheduled_date || "").localeCompare(b.scheduled_date || ""));
    }
    return items.filter(b => b.status === filter);
  }, [items, filter, todayStr]);

  // Bulk selection
  const visibleIds = useMemo(() => visible.map(b => b.id), [visible]);
  const bulk = useBulkSelect(visibleIds);

  async function bulkRun(ids, fn, { successMsg }) {
    const results = await Promise.allSettled(ids.map(fn));
    const ok = results.filter(r => r.status === "fulfilled").length;
    const ko = results.length - ok;
    if (ok > 0) toast.success(`${ok} ${successMsg}${ko > 0 ? ` · ${ko} falharam` : ""}`);
    else toast.error("Nenhuma operação concluída");
    load();
  }

  async function updateStatus(id, patch) {
    try {
      await api.patch(`/bookings/${id}/status`, patch);
      toast.success("Atualizado");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.reason === "employee_overlap") {
        toast.error(`${detail.message} Próximo horário livre: ${detail.next_available_after}.`);
      } else {
        toast.error(formatApiError(e));
      }
    }
  }

  async function markPayment(id, payment_status, options = {}) {
    const { skipInvoice = false, paymentMethod } = options;
    try {
      const params = { payment_status };
      if (skipInvoice) params.skip_invoice = true;
      if (paymentMethod) params.payment_method = paymentMethod;
      await api.patch(`/admin/bookings/${id}/payment`, null, { params });
      toast.success(
        payment_status === "paid"
          ? (skipInvoice
              ? "Pagamento em dinheiro registado. Sem fatura emitida."
              : "Pagamento confirmado. A emitir Fatura-Recibo via Facturalusa…")
          : "Estado de pagamento atualizado.",
      );
      load();
      // Quick check 6s later: did Facturalusa emit successfully?
      if (payment_status === "paid" && !skipInvoice) {
        setTimeout(async () => {
          try {
            const { data } = await api.get(`/admin/facturalusa/invoices/${id}`);
            if (data.status === "issued" && data.invoice_number) {
              toast.success(`Fatura ${data.invoice_number} emitida.`);
            } else if (data.status === "error") {
              toast.error(`Facturalusa: ${data.last_error || "erro desconhecido"}`);
            }
          } catch { /* silent */ }
        }, 6000);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function deleteBooking(id) {
    if (!window.confirm("Eliminar esta reserva? Esta ação é irreversível.")) return;
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Reserva eliminada");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function resendReport(id) {
    try {
      await api.post(`/admin/bookings/${id}/report/resend`);
      toast.success("Relatório reenviado ao cliente (email + app).");
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function extraInfo(id) {
    return extrasCatalog.find(e => e.id === id);
  }

  return (
    <div className="space-y-8">
      <BookingsKillSwitch />
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-3">Operação</div>
          <h1 className="font-display text-5xl">Reservas</h1>
          <div className="text-sm text-[#a0b2af] mt-2">
            Gestão central · crie reservas em nome de qualquer cliente.
          </div>
        </div>
        <Link to="/admin/reservas/nova" className="btn-gold px-5 py-3 text-xs uppercase tracking-widest inline-flex items-center gap-2"
              data-testid="admin-new-booking-btn">
          <Plus size={14} /> Nova reserva
        </Link>
      </div>

      {/* Status filter tabs */}
      <div className="flex flex-wrap gap-2 items-center" data-testid="admin-bookings-filters">
        {visible.length > 0 && (
          <label className="inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-widest text-[#a0b2af] border border-[#1a4640] rounded-sm cursor-pointer hover:text-[#d4af37] hover:border-[#d4af37]/50"
                 title={bulk.allSelected ? "Desselecionar todos" : "Selecionar todos"}>
            <BulkCheckbox
              checked={bulk.allSelected}
              indeterminate={bulk.someSelected}
              onChange={() => bulk.toggleAll(visibleIds)}
              label="Selecionar todos"
            />
            Tudo
          </label>
        )}
        {FILTERS.map(f => {
          const active = filter === f.key;
          const count = counts[f.key] ?? 0;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 text-xs uppercase tracking-widest border rounded-sm inline-flex items-center gap-2 transition-colors
                ${active
                  ? "border-[#d4af37] text-[#d4af37] bg-[#062c27]"
                  : "border-[#1a4640] text-[#a0b2af] hover:text-[#fdfbf7] hover:border-[#d4af37]/50"}`}
              data-testid={`filter-${f.key}`}>
              <span>{f.label}</span>
              <span className={`min-w-[22px] text-center px-1.5 py-0.5 rounded-sm text-[10px] font-mono
                ${active ? "bg-[#d4af37] text-[#041f1b]" : "bg-[#1a4640] text-[#a0b2af]"}`}
                    data-testid={`filter-count-${f.key}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="space-y-3">
        {visible.length === 0 && (
          <div className="lux-card p-10 text-center text-[#a0b2af] text-sm" data-testid="admin-bookings-empty">
            {filter === "all" ? "Sem reservas."
              : filter === "overdue" ? "Sem reservas atrasadas — tudo em dia."
              : `Sem reservas com o estado "${FILTERS.find(f => f.key === filter)?.label}".`}
          </div>
        )}
        {visible.map(b => {
          const isOpen = !!expanded[b.id];
          const isToday = b.scheduled_date === todayStr;
          const isOverdue = b.scheduled_date < todayStr
            && ["pending", "confirmed", "in_progress"].includes(b.status);
          const daysLate = isOverdue
            ? Math.max(0, Math.round((new Date(todayStr) - new Date(b.scheduled_date)) / 86400000))
            : 0;
          return (
            <div key={b.id}
                 className={`lux-card overflow-hidden transition-shadow ${
                   isOverdue ? "ring-2 ring-[#ef4444]/60 bg-[#ef4444]/5"
                   : isToday ? "ring-2 ring-[#d4af37] bg-[#d4af37]/5 shadow-[0_0_24px_rgba(212,175,55,0.15)]" : ""
                 }`}
                 data-testid={`admin-booking-${b.id}`}>
              {isOverdue && (
                <div className="bg-[#ef4444] text-[#fdfbf7] text-[10px] uppercase tracking-widest font-semibold px-5 py-1.5 inline-flex items-center gap-2"
                     data-testid={`admin-overdue-badge-${b.id}`}>
                  ⚠ Atrasada · {daysLate === 0 ? "hoje" : `${daysLate} dia${daysLate > 1 ? "s" : ""}`} · aguarda conclusão
                </div>
              )}
              {!isOverdue && isToday && (
                <div className="bg-[#d4af37] text-[#041f1b] text-[10px] uppercase tracking-widest font-semibold px-5 py-1.5" data-testid={`admin-today-badge-${b.id}`}>
                  Hoje · {b.scheduled_time}
                </div>
              )}
              <div className="p-5">
                <div className="grid md:grid-cols-6 gap-4 items-center">
                  <div className="flex items-start gap-3">
                    <div className="pt-1">
                      <BulkCheckbox
                        id={b.id}
                        checked={bulk.isSelected(b.id)}
                        onChange={() => bulk.toggle(b.id)}
                        label="Selecionar reserva"
                      />
                    </div>
                    <div>
                      <div className="eyebrow">{b.apt_type}</div>
                      <div className="text-sm mt-1">{b.scheduled_date} · {b.scheduled_time}</div>
                      {b.apartment_name && <div className="text-xs text-[#d4af37] mt-1">{b.apartment_name}</div>}
                      {b.apartment_address && (
                        <div className="text-[10px] text-[#a0b2af] mt-0.5 truncate" title={b.apartment_address}>
                          📍 {b.apartment_address}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-sm">{b.client_name}</div>
                    {b.client_phone && (
                      <a href={`tel:${b.client_phone}`} className="text-xs text-[#d4af37] hover:underline mt-0.5 inline-block" data-testid={`admin-phone-${b.id}`}>
                        📞 {b.client_phone}
                      </a>
                    )}
                    {b.client_email && (
                      <div className="text-[10px] text-[#a0b2af] truncate">{b.client_email}</div>
                    )}
                    {b.notes && <div className="text-[10px] text-[#a0b2af] mt-1 italic truncate">📝 {b.notes}</div>}
                  </div>
                  <div>
                    <div className="font-display text-xl text-[#d4af37]">{b.total.toFixed(2)}€</div>
                    <div className="text-xs text-[#a0b2af]">{b.extras.length} extras · {Math.floor(b.duration / 60)}h{b.duration % 60 ? ` ${b.duration % 60}m` : ""}</div>
                  </div>
                  <div>
                    <select value={b.assigned_employee_id || ""}
                            onChange={e => updateStatus(b.id, { status: b.status, assigned_employee_id: e.target.value || null })}
                            className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-3 py-2 text-sm"
                            data-testid={`assign-${b.id}`}>
                      <option value="">— Atribuir —</option>
                      {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                    </select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className={`text-xs uppercase tracking-widest px-2 py-1 rounded-sm inline-block ev-${mapStatus(b.status)}`}>{STATUS_LABEL[b.status] || b.status}</span>
                    {b.payment_status && (
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm inline-block ${b.payment_status === "paid" ? "text-[#10b981] border border-[#10b981]/40" : b.payment_status === "refunded" ? "text-[#a0b2af] border border-[#1a4640]" : "text-[#f59e0b] border border-[#f59e0b]/40"}`}
                            data-testid={`payment-badge-${b.id}`}
                            title={b.payment_reference ? `Ref: ${b.payment_reference}` : ""}>
                        {b.payment_status === "paid" ? "Pago" : b.payment_status === "refunded" ? "Reembolsado" : "Pag. pendente"}
                      </span>
                    )}
                    <div className="flex gap-2 flex-wrap">
                      {b.payment_status !== "paid" && (
                        <button onClick={() => setPreviewBookingId(b.id)}
                                className="text-xs btn-gold px-2 py-1"
                                data-testid={`mark-paid-${b.id}`}
                                title="Pré-visualizar a Fatura-Recibo antes de emitir">
                          Marcar pago + emitir fatura
                        </button>
                      )}
                      {b.status === "pending" && (
                        <button onClick={() => updateStatus(b.id, { status: "confirmed" })} className="text-xs btn-ghost-gold px-2 py-1" data-testid={`confirm-${b.id}`}>Confirmar</button>
                      )}
                      {["pending", "confirmed"].includes(b.status) && (
                        <>
                          <button onClick={() => setEditingBooking(b)}
                                  className="text-xs btn-ghost-gold px-2 py-1 inline-flex items-center gap-1"
                                  data-testid={`edit-booking-${b.id}`}
                                  title="Alterar data/hora/notas">
                            <CalendarClock size={12} /> Alterar
                          </button>
                          <button onClick={() => updateStatus(b.id, { status: "cancelled" })} className="text-xs text-[#fca5a5] border border-[#ef4444]/40 px-2 py-1" data-testid={`cancel-${b.id}`}>Cancelar</button>
                        </>
                      )}
                      {b.status === "in_progress" && (
                        <button
                          onClick={() => {
                            if (window.confirm("Marcar como concluída?\n\nUse só se o relatório do funcionário ficou preso (ex: falta de fotos). Não envia novo relatório ao cliente.")) {
                              updateStatus(b.id, { status: "completed" });
                            }
                          }}
                          className="text-xs btn-ghost-gold px-2 py-1"
                          data-testid={`force-complete-${b.id}`}
                          title="Forçar conclusão (use quando o relatório do funcionário falhou)"
                        >
                          Forçar conclusão
                        </button>
                      )}
                      {b.status === "completed" && (
                        <>
                          <button onClick={() => setConsumptionBookingId(b.id)}
                                  className="text-xs btn-ghost-gold px-2 py-1 inline-flex items-center gap-1"
                                  data-testid={`admin-consumption-${b.id}`}
                                  title="Registar produtos/materiais usados nesta limpeza (cálculo de custo)">
                            {b.consumption_logged ? "✓ Consumo registado" : "Registar consumo"}
                          </button>
                          <button onClick={() => resendReport(b.id)}
                                  className="text-xs btn-ghost-gold px-2 py-1"
                                  data-testid={`resend-report-${b.id}`}
                                  title="Reenviar relatório de limpeza ao cliente (email + app)">
                            Reenviar relatório
                          </button>
                        </>
                      )}
                      <button onClick={() => deleteBooking(b.id)}
                              className="text-xs text-[#fca5a5] border border-[#ef4444]/60 px-2 py-1 inline-flex items-center gap-1 hover:bg-[#ef4444]/10 transition-colors"
                              data-testid={`delete-booking-${b.id}`}
                              title="Eliminar reserva (irreversível)">
                        <Trash2 size={12} /> Eliminar
                      </button>
                    </div>
                  </div>
                </div>

                <button onClick={() => setExpanded(ex => ({ ...ex, [b.id]: !ex[b.id] }))}
                        className="mt-4 text-xs text-[#a0b2af] hover:text-[#d4af37] inline-flex items-center gap-1"
                        data-testid={`toggle-${b.id}`}>
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  {isOpen ? "Ocultar detalhes" : "Ver detalhes completos"}
                </button>
              </div>

              {isOpen && (
                <div className="px-5 pb-5 pt-2 bg-[#041f1b]/60 border-t border-[#1a4640] grid md:grid-cols-2 gap-6 text-sm" data-testid={`details-${b.id}`}>
                  <section>
                    <div className="eyebrow mb-3">Cliente & Morada</div>
                    <dl className="space-y-2">
                      <Field k="Cliente" v={<span className="font-medium">{b.client_name}</span>} />
                      {b.client_registration_number && <Field k="Nº Cliente" v={<span className="font-mono text-[#d4af37]">{b.client_registration_number}</span>} />}
                      {b.client_phone && <Field k="Telefone" v={<a href={`tel:${b.client_phone}`} className="text-[#d4af37] hover:underline">{b.client_phone}</a>} />}
                      {b.client_email && <Field k="Email" v={<a href={`mailto:${b.client_email}`} className="text-[#d4af37] hover:underline text-xs">{b.client_email}</a>} />}
                      <Field k="Apartamento" v={b.apartment_name || "—"} />
                      {b.apartment_address && <Field k="Morada" v={<span className="text-right block max-w-[70%]">{b.apartment_address}</span>} />}
                    </dl>
                  </section>
                  <section>
                    <div className="eyebrow mb-3">Tipologia & Duração</div>
                    <dl className="space-y-2">
                      <Field k="Tipo" v={<span className="font-display text-lg">{b.apt_type}</span>} />
                      <Field k="Duração" v={`${Math.floor(b.duration / 60)}h${b.duration % 60 ? ` ${b.duration % 60}m` : ""}`} />
                      <Field k="Data" v={b.scheduled_date} />
                      <Field k="Hora" v={b.scheduled_time} />
                      <Field k="Funcionário atribuído" v={employees.find(e => e.id === b.assigned_employee_id)?.name || "—"} />
                    </dl>
                  </section>
                  <section className="md:col-span-2">
                    <div className="eyebrow mb-3">Serviços & Extras a realizar</div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between py-2 border-b border-[#1a4640]">
                        <span className="text-[#fdfbf7]">Limpeza base {b.apt_type}</span>
                        <span className="text-[#d4af37]">{b.base_price.toFixed(2)}€</span>
                      </div>
                      {b.extras.length === 0 && <div className="text-[#a0b2af] text-xs py-2">Sem extras.</div>}
                      {b.extras.map(eid => {
                        const ex = extraInfo(eid);
                        if (!ex) return null;
                        const override = (b.extras_overrides || {})[eid];
                        const effective = override != null ? Number(override) : ex.price;
                        return (
                          <ExtraRow
                            key={eid}
                            eid={eid}
                            name={ex.name}
                            icon={EXTRAS_ICONS[eid] || "✦"}
                            priceOnRequest={!!ex.price_on_request}
                            effective={effective}
                            hasOverride={override != null}
                            bookingId={b.id}
                            onSaved={load}
                          />
                        );
                      })}
                      <div className="flex items-center justify-between pt-3">
                        <span className="eyebrow">Total</span>
                        <span className="font-display text-2xl text-[#d4af37]">{b.total.toFixed(2)}€</span>
                      </div>
                      {b.promo_applied && <div className="text-xs text-[#10b981]">Preço promocional aplicado ✓</div>}
                    </div>
                  </section>
                  {b.notes && (
                    <section className="md:col-span-2">
                      <div className="eyebrow mb-2">Notas do cliente</div>
                      <div className="text-[#fdfbf7] p-3 border border-[#1a4640] bg-[#062c27] rounded-sm">{b.notes}</div>
                    </section>
                  )}
                  {b.extras_notes && Object.keys(b.extras_notes).length > 0 && (
                    <section className="md:col-span-2" data-testid={`admin-extras-notes-${b.id}`}>
                      <div className="eyebrow mb-2">Descrição dos extras a combinar</div>
                      <div className="space-y-2">
                        {Object.entries(b.extras_notes).map(([k, v]) => (
                          <div key={k} className="p-3 border border-[#d4af37]/30 bg-[#0a3a33]/40 rounded-sm">
                            <div className="text-[10px] uppercase tracking-widest text-[#d4af37] mb-1">
                              {k === "outros" ? "Outros" : k}
                            </div>
                            <div className="text-sm text-[#fdfbf7]">{v}</div>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <BulkActionBar
        count={bulk.count}
        ids={Array.from(bulk.selected)}
        onClear={bulk.clear}
        actions={[
          {
            key: "confirm", label: "Confirmar", icon: Check, variant: "gold",
            confirm: "Confirmar {count} reserva(s)?",
            onClick: (ids) => bulkRun(ids, (id) => api.patch(`/bookings/${id}/status`, { status: "confirmed" }), { successMsg: "confirmada(s)" }),
          },
          {
            key: "cancel", label: "Cancelar", icon: XIcon, variant: "danger",
            confirm: "Cancelar {count} reserva(s)?",
            onClick: (ids) => bulkRun(ids, (id) => api.patch(`/bookings/${id}/status`, { status: "cancelled" }), { successMsg: "cancelada(s)" }),
          },
          {
            key: "delete", label: "Eliminar", icon: Trash2, variant: "danger",
            confirm: "Eliminar {count} reserva(s)? Ação irreversível.",
            onClick: (ids) => bulkRun(ids, (id) => api.delete(`/bookings/${id}`), { successMsg: "eliminada(s)" }),
          },
        ]}
      />

      {editingBooking && (
        <RescheduleModal
          booking={editingBooking}
          isAdmin={true}
          onClose={() => setEditingBooking(null)}
          onSaved={() => { setEditingBooking(null); load(); }}
        />
      )}
      <CleaningConsumptionModal
        bookingId={consumptionBookingId}
        open={!!consumptionBookingId}
        onClose={() => setConsumptionBookingId(null)}
        onSkip={() => setConsumptionBookingId(null)}
        onSubmitted={() => { setConsumptionBookingId(null); load(); }}
      />

      {/* Pré-visualização da fatura antes de emitir */}
      {previewBookingId && (
        <InvoicePreviewModal
          bookingId={previewBookingId}
          onClose={() => setPreviewBookingId(null)}
          onConfirm={async ({ skipInvoice = false, paymentMethod } = {}) => {
            const id = previewBookingId;
            setPreviewBookingId(null);
            await markPayment(id, "paid", { skipInvoice, paymentMethod });
          }}
        />
      )}
    </div>
  );
}

function Field({ k, v }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-[#a0b2af] text-xs uppercase tracking-widest">{k}</dt>
      <dd>{v}</dd>
    </div>
  );
}

function mapStatus(s) {
  if (s === "confirmed" || s === "in_progress") return "confirmed";
  if (s === "completed") return "completed";
  if (s === "cancelled") return "blocked";
  return "pending";
}


function ExtraRow({ eid, name, icon, priceOnRequest, effective, hasOverride, bookingId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(effective || ""));
  const [saving, setSaving] = useState(false);

  async function save() {
    const v = parseFloat(value);
    if (Number.isNaN(v) || v < 0) { toast.error("Valor inválido"); return; }
    setSaving(true);
    try {
      await api.patch(`/admin/bookings/${bookingId}/extra-price`, { extra_id: eid, price: v });
      toast.success("Valor atualizado");
      setEditing(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  if (editing) {
    return (
      <div className="flex items-center justify-between py-2 border-b border-[#1a4640]/50 gap-2">
        <span className="inline-flex items-center gap-2 min-w-0">
          <span className="text-lg">{icon}</span>
          <span className="truncate">{name}</span>
        </span>
        <div className="flex items-center gap-1.5 shrink-0">
          <Euro size={12} className="text-[#d4af37]" />
          <input type="number" step="0.01" min="0" value={value}
                 onChange={(e) => setValue(e.target.value)}
                 className="w-20 bg-[#031c18] border border-[#d4af37] rounded-sm px-2 py-1 text-sm text-[#d4af37] font-mono text-right"
                 autoFocus />
          <button onClick={save} disabled={saving}
                  className="w-7 h-7 rounded-sm bg-[#d4af37] text-[#041f1b] hover:bg-[#d4af37]/90 disabled:opacity-50 flex items-center justify-center"
                  data-testid={`extra-price-save-${eid}`}>
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
          </button>
          <button onClick={() => setEditing(false)}
                  className="w-7 h-7 rounded-sm border border-[#1a4640] text-[#a0b2af] hover:text-[#ef4444] flex items-center justify-center">
            <XIcon size={12} />
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#1a4640]/50 group">
      <span className="inline-flex items-center gap-2 min-w-0">
        <span className="text-lg">{icon}</span>
        <span className="truncate">{name}</span>
      </span>
      <div className="flex items-center gap-2 shrink-0">
        <span className={priceOnRequest && !hasOverride ? "text-[#f59e0b]" : "text-[#d4af37]"}>
          {priceOnRequest && !hasOverride ? "A combinar" : `+${effective.toFixed(2)}€`}
        </span>
        <button onClick={() => setEditing(true)}
                className="p-1 text-[#a0b2af] hover:text-[#d4af37] opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`extra-price-edit-${eid}`}
                title="Editar valor">
          <Pencil size={12} />
        </button>
      </div>
    </div>
  );
}
